import partition from "lodash/partition";
import { useRouter } from "next/router";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

import { ProductPageProps } from "app/smartphone/[slug]/[reference]/page";

export enum ConditionOffer {
  REFURBISHED = "refurbished",
  NEW = "new",
}

type ProductData = Pick<ProductPageProps, "productData" | "settings"> & {
  referenceSlug: string;
};

type ProductPageContextVariantData = {
  currentVariant?: Variant;
  brandNewVariants: Variant[];
  refurbishedVariants: Variant[];
  selectedOfferCondition: ConditionOffer;
  setCurrentVariant: Dispatch<SetStateAction<Variant | undefined>>;
  setSelectedOfferCondition: Dispatch<SetStateAction<ConditionOffer>>;
};

type ProductPageContextData =
  | (ProductPageProps["productData"] &
      ProductPageProps["settings"] &
      ProductPageContextVariantData)
  | undefined;

export const ProductPageContext =
  createContext<ProductPageContextData>(undefined);

type ProductPageContextProps = ProductData;

type Variant = NonNullable<
  ProductPageContextProps["productData"]["variants"]
>[number];

const autoChooseCondition = (
  brandNewVariants: Variant[],
  refurbishedVariants: Variant[],
): ConditionOffer => {
  // Check if at least one of the lists contains variants
  const hasBrandNewVariants = brandNewVariants?.length > 0;
  const hasRefurbishedVariants = refurbishedVariants?.length > 0;

  // If neither list has variants, return REFURBISHED condition
  if (!hasBrandNewVariants && !hasRefurbishedVariants) {
    return ConditionOffer.REFURBISHED;
  }

  // If only one list has variants, return that one's condition
  if (hasBrandNewVariants && !hasRefurbishedVariants) {
    return ConditionOffer.NEW;
  }
  if (!hasBrandNewVariants && hasRefurbishedVariants) {
    return ConditionOffer.REFURBISHED;
  }

  // If both lists have variants, check if one of them has a variant in stock
  const brandNewHasInStock = brandNewVariants.some(
    (variant) => variant.isInStock,
  );
  const refurbishedHasInStock = refurbishedVariants.some(
    (variant) => variant.isInStock,
  );

  if (refurbishedHasInStock) {
    return ConditionOffer.REFURBISHED;
  }
  if (brandNewHasInStock) {
    return ConditionOffer.NEW;
  }

  // Default return NEW condition
  return ConditionOffer.NEW;
};

export const ProductPageContextProvider = ({
  children,
  productData,
  referenceSlug,
  settings,
}: PropsWithChildren<ProductPageContextProps>) => {
  const { query } = useRouter();

  const [brandNewVariants, refurbishedVariants] = partition(
    productData?.variants,
    (v) => v.mustBePurchased,
  );

  const autoChosenCondition = autoChooseCondition(
    brandNewVariants,
    refurbishedVariants,
  );

  const [selectedOfferCondition, setSelectedOfferCondition] =
    useState<ConditionOffer>(
      (query?.condition as ConditionOffer) || autoChosenCondition,
    );

  const mustBePurchasedSelected = selectedOfferCondition === ConditionOffer.NEW;

  const getInitialVariant = useCallback(() => {
    const filteredVariants = productData?.variants
      ?.filter((variant) => variant.mustBePurchased === mustBePurchasedSelected)
      .sort((a, b) => {
        if (a.commitmentPeriod === undefined) return 1;
        if (b.commitmentPeriod === undefined) return -1;
        return b.commitmentPeriod - a.commitmentPeriod;
      });

    if (!filteredVariants || filteredVariants.length === 0) {
      return productData?.variants?.[0];
    }

    const matchingVariant = filteredVariants.find(
      (variant) =>
        variant.variantSlug === ((query?.reference as string) || referenceSlug),
    );

    return matchingVariant || filteredVariants[0];
  }, [
    mustBePurchasedSelected,
    productData?.variants,
    query?.reference,
    referenceSlug,
  ]);

  const [currentVariant, setCurrentVariant] = useState(getInitialVariant());

  useEffect(() => {
    setCurrentVariant(getInitialVariant());
  }, [getInitialVariant]);

  return (
    <ProductPageContext.Provider
      value={{
        ...productData,
        ...settings,
        brandNewVariants,
        refurbishedVariants,
        currentVariant,
        setCurrentVariant,
        selectedOfferCondition,
        setSelectedOfferCondition,
      }}
    >
      {children}
    </ProductPageContext.Provider>
  );
};
